import { CloseButton } from "../../dispatching-planning/common/close-button";
import { Button } from "@mui/material";
import SavedLocationRow from "./SavedLocationRow";
import DeliveryLocationRow from "./DeliveryLocationRow";
import SavedLocation, { SavedLocationSelectionType } from "./SavedLocation";
import { useEffect, useState } from "react";
import SavedLocationTypeSelectionOption from "./SavedLocationTypeSelectionOption";
import { useAppDispatch, useSelector } from "../../../redux-store";
import {
  selectFavoriteLocations,
  selectTerminalLocations,
} from "../../../ducks/data/saved-locations/selectors";
import { loadSavedLocations } from "../../../ducks/data/saved-locations";
import { useTranslation } from "react-i18next";
import { DoraTextInput } from "dora-shared/frontend";
import { selectFinanceConfiguration } from "../../../ducks/data/finance/selectors";
import { CustomerDeliveryLocation } from "dora-contracts";

const ManageSavedLocations = ({
  onAddClick,
  onEditClick,
  onTypeChange,
  type,
  onClose,
  customerDeliveryAddresses,
  clientId,
}: {
  onAddClick: () => void;
  onEditClick: (location: SavedLocation) => void;
  onTypeChange: (type: SavedLocationSelectionType) => void;
  type: SavedLocationSelectionType;
  onClose: () => void;
  customerDeliveryAddresses?: CustomerDeliveryLocation[];
  clientId?: string;
}) => {
  const { t } = useTranslation(["modals", "components"]);

  const [selectionType, setSelectionType] =
    useState<SavedLocationSelectionType>(type);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const terminals = useSelector(selectTerminalLocations);
  const favorites = useSelector(selectFavoriteLocations);
  const financeConfig = useSelector(selectFinanceConfiguration);
  const showDeliveryLocationsTab =
    financeConfig === "E-CONOMIC" && !!clientId;
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(loadSavedLocations());
  }, [dispatch]);

  const onCategoryClick = (type: SavedLocationSelectionType) => {
    setPage(1);
    setSelectionType(type);
    onTypeChange(type);
    dispatch(loadSavedLocations());
  };

  const getDeliveryLocations = () => {
    let locations = customerDeliveryAddresses ?? [];
    if (search) {
      locations = locations.filter((location) =>
        [
          location.name,
          location.number,
          location.address,
          location.postalCode,
          location.city,
          location.country,
        ].some((value) => value?.toLowerCase().includes(search))
      );
    }
    return locations.slice((page - 1) * 7, page * 7);
  };

  const getLocations = () => {
    let locations = selectionType === "TERMINAL" ? terminals : favorites;
    if (search) {
      locations = locations.filter((location) =>
        search
          ? location.place.city?.toLowerCase().includes(search) ||
            location.place.country?.toLowerCase().includes(search) ||
            location.place.postcode?.toLowerCase().includes(search) ||
            location.place.address?.toLowerCase().includes(search) ||
            location.place.placeName?.toLowerCase().includes(search) ||
            location.customName.includes(search.toLowerCase())
          : true
      );
    }
    return locations.slice((page - 1) * 7, page * 7);
  };

  const totalPages = Math.ceil(
    (selectionType === "DELIVERY_ADDRESS"
      ? (customerDeliveryAddresses ?? [])
      : selectionType === "TERMINAL"
        ? terminals
        : favorites
    ).length / 7
  );

  return (
    <div className="favorites-terminals-dialog__container">
      <div className="favorites-terminals-dialog__title">
        <div>{t("modals:manageSavedLocations.manageTitle")}</div>
        <CloseButton onClick={onClose} />
      </div>
      <div className="favorites-terminals-dialog__subtitle">
        {t("modals:manageSavedLocations.description")}
      </div>
      <div className="favorites-terminals-dialog__first-row">
        <div style={{ display: "flex" }} role="radiogroup">
          <SavedLocationTypeSelectionOption
            type={"favorite"}
            active={selectionType === "FAVORITE"}
            onClick={() => onCategoryClick("FAVORITE")}
          />
          <SavedLocationTypeSelectionOption
            type={"terminal"}
            active={selectionType === "TERMINAL"}
            onClick={() => onCategoryClick("TERMINAL")}
          />
          {showDeliveryLocationsTab && (
            <SavedLocationTypeSelectionOption
              type={"delivery"}
              active={selectionType === "DELIVERY_ADDRESS"}
              onClick={() => onCategoryClick("DELIVERY_ADDRESS")}
            />
          )}
        </div>
        <Button variant="contained" onClick={onAddClick}>
          + {t("modals:manageSavedLocations.addButton")}
        </Button>
      </div>
      <div className="favorites-terminals-dialog__location-text">
        {t("modals:manageSavedLocations.locations")}
      </div>

      <div className="px-6 py-2">
        <DoraTextInput
          onChange={(s) => setSearch(s.toLowerCase().trim())}
          placeholder={t("components:buttonLabels.search")}
        ></DoraTextInput>
      </div>

      {selectionType === "DELIVERY_ADDRESS"
        ? getDeliveryLocations().map((location) => (
            <div key={location.id}>
              <hr className="favorites-terminals-dialog__hr" />
              <DeliveryLocationRow location={location} />
            </div>
          ))
        : getLocations().map((location, i) => (
            <div key={i}>
              <hr className="favorites-terminals-dialog__hr" />
              <SavedLocationRow
                location={location}
                onEditClick={() => onEditClick(location)}
              ></SavedLocationRow>
            </div>
          ))}
      <hr className="favorites-terminals-dialog__hr" />
      <div className="favorites-terminals-dialog__bottom-row">
        <div>
          {t("modals:manageSavedLocations.pagination", {
            currentPage: page,
            totalPages,
          })}
        </div>
        <div className="favorites-terminals-dialog__pagination-buttons">
          <Button
            variant="outlined"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            {t("modals:manageSavedLocations.previousButton")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages || totalPages === 0}
          >
            {t("modals:manageSavedLocations.nextButton")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManageSavedLocations;
